// frontend/src/pages/Checkout.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { createOrder } from "../api";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLock,
  faTruck,
  faCreditCard,
  faMapMarkerAlt,
  faShoppingCart,
  faSeedling,
  faCheckCircle,
  faSpinner,
  faWhatsapp,
  faMobileAlt,
  faMoneyBillWave,
  faBuilding,
  faCalendarCheck,
  faBox,
  faGlobe,
  faShip,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// MAIN COMPONENT
// ============================================
const Checkout = () => {
  const { cart, loading: cartLoading, emptyCart } = useCart();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [formData, setFormData] = useState({
    shippingAddress: {
      recipientName: user?.name || "",
      street: user?.addresses?.[0]?.street || "",
      city: user?.addresses?.[0]?.city || "",
      district: user?.addresses?.[0]?.district || "",
      country: "Sierra Leone",
      phone: user?.phone || "",
    },
    paymentMethod: "cash_on_delivery",
    notes: "",
  });

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    if (!cartLoading && (!cart?.items || cart.items.length === 0)) {
      navigate("/cart");
    }
  }, [cart, cartLoading, navigate]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({
        ...formData,
        [parent]: { ...formData[parent], [child]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const orderItems = cart.items.map((item) => ({
      productId: item.product?._id || item.productId,
      quantity: item.quantity,
    }));

    try {
      const response = await createOrder({
        items: orderItems,
        shippingAddress: formData.shippingAddress,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
        currency,
        supplier: {
          name: SUPPLIER.name,
          country: SUPPLIER.country,
        },
      });

      const orderData = response.data.data || response.data;
      setOrderId(orderData._id);
      setOrderSuccess(true);
      await emptyCart();

      setTimeout(() => {
        navigate(`/orders/${orderData._id}`);
      }, 3000);
    } catch (error) {
      console.error("Order creation error:", error);
      alert(
        error.response?.data?.message ||
          "Error creating order. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // COMPUTED
  // ============================================
  if (cartLoading) return <Loader />;
  if (!cart?.items || cart.items.length === 0) return null;

  const subtotalUSD =
    cart?.items?.reduce(
      (sum, item) => sum + (item.price?.usd || 0) * item.quantity,
      0,
    ) || 0;
  const totalQuantity =
    cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const isBulkOrder = totalQuantity >= 100;

  // ============================================
  // SUCCESS STATE
  // ============================================
  if (orderSuccess) {
    return (
      <motion.div
        className="checkout-success"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="checkout-success__icon">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
        <h1>Order Placed Successfully!</h1>
        <p>
          Thank you for your order. We'll send you a confirmation email shortly.
        </p>
        <p className="checkout-success__order">Order #{orderId?.slice(-8) || "Processing"}</p>
        <div className="checkout-success__actions">
          <Button href={`/orders/${orderId}`} variant="primary">
            View Order Details
          </Button>
          <Button href="/products" variant="outline">
            Continue Shopping
          </Button>
        </div>
        <style>{`
          .checkout-success {
            padding: 4rem 1.5rem;
            max-width: 600px;
            margin: 0 auto;
            text-align: center;
          }
          .checkout-success__icon {
            font-size: 4rem;
            margin-bottom: 1.5rem;
          }
          .checkout-success__icon svg {
            color: #16a34a;
          }
          .checkout-success h1 {
            color: #006B2E;
            margin-bottom: 1rem;
            font-family: "Montserrat", sans-serif;
          }
          .checkout-success p {
            color: #5A5A5A;
          }
          .checkout-success__order {
            margin-bottom: 2rem;
          }
          .checkout-success__actions {
            display: flex;
            gap: 1rem;
            justify-content: center;
            flex-wrap: wrap;
          }
          @media (max-width: 480px) {
            .checkout-success {
              padding: 2rem 1rem;
            }
            .checkout-success__actions {
              flex-direction: column;
              align-items: stretch;
            }
          }
        `}</style>
      </motion.div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="checkout-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Hero */}
      <section className="checkout-hero">
        <div className="checkout-hero__overlay" />
        <div className="container checkout-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faShoppingCart} className="checkout-hero__icon" />
            <h1>Checkout</h1>
            <p>
              Review your order and complete your purchase from Kenali Enterprise
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Form */}
      <div className="container checkout-container">
        <Link to="/cart" className="checkout-back">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Cart
        </Link>

        <form onSubmit={handleSubmit}>
          <div className="checkout-grid">
            {/* Left Column - Forms */}
            <div className="checkout-forms">
              {/* Shipping Address */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                <h3>
                  <FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address
                </h3>
                <div className="form-group">
                  <label>Recipient Name *</label>
                  <input
                    type="text"
                    name="shippingAddress.recipientName"
                    value={formData.shippingAddress.recipientName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="shippingAddress.street"
                    value={formData.shippingAddress.street}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      name="shippingAddress.city"
                      value={formData.shippingAddress.city}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>District *</label>
                    <input
                      type="text"
                      name="shippingAddress.district"
                      value={formData.shippingAddress.district}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="shippingAddress.phone"
                    value={formData.shippingAddress.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </motion.div>

              {/* Payment Method */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <h3>
                  <FontAwesomeIcon icon={faCreditCard} /> Payment Method
                </h3>
                <div className="payment-options">
                  {[
                    { value: "cash_on_delivery", label: "Cash on Delivery", icon: faMoneyBillWave },
                    { value: "bank_transfer", label: "Bank Transfer", icon: faBuilding },
                    { value: "orange_money", label: "Orange Money", icon: faMobileAlt },
                    { value: "afrimoney", label: "Afrimoney", icon: faMobileAlt },
                    { value: "wave", label: "WAVE", icon: faMobileAlt },
                  ].map((method) => (
                    <label
                      key={method.value}
                      className={`payment-option ${formData.paymentMethod === method.value ? "selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.value}
                        checked={formData.paymentMethod === method.value}
                        onChange={handleChange}
                      />
                      <FontAwesomeIcon icon={method.icon} />
                      {method.label}
                    </label>
                  ))}
                </div>
              </motion.div>

              {/* Order Notes */}
              <motion.div
                className="checkout-section"
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3>Order Notes (Optional)</h3>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Special delivery instructions or notes about your order..."
                />
              </motion.div>
            </div>

            {/* Right Column - Summary */}
            <motion.div
              className="checkout-summary"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h3>
                <FontAwesomeIcon icon={faShoppingCart} /> Order Summary
              </h3>

              {isBulkOrder && (
                <div className="bulk-badge">
                  🌾 Bulk Order ({totalQuantity}kg) - Priority Processing
                </div>
              )}

              <div className="summary-items">
                {cart.items.map((item) => (
                  <div key={item._id} className="summary-item">
                    <span>
                      {item.name} × {item.quantity}
                    </span>
                    <span>
                      <CurrencyDisplay
                        amountUSD={(item.price?.usd || 0) * item.quantity}
                        compact
                      />
                    </span>
                  </div>
                ))}
              </div>

              <hr className="summary-divider" />

              <div className="total-row">
                <span>Subtotal:</span>
                <span>
                  <CurrencyDisplay amountUSD={subtotalUSD} />
                </span>
              </div>
              <div className="total-row shipping">
                <span>Shipping:</span>
                <span>
                  {isBulkOrder || subtotalUSD >= 100
                    ? "Free"
                    : "Calculated at delivery"}
                </span>
              </div>
              <div className="total-row supplier-info">
                <span>
                  <FontAwesomeIcon icon={faShip} /> Supplier:
                </span>
                <span>{SUPPLIER.name} ({SUPPLIER.country})</span>
              </div>

              <hr className="summary-divider" />

              <div className="total-row grand-total">
                <span>Total:</span>
                <span>
                  <CurrencyDisplay amountUSD={subtotalUSD} />
                </span>
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={submitting}
                fullWidth
                size="large"
              >
                {submitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Placing Order...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faLock} /> Place Order
                  </>
                )}
              </Button>

              <div className="summary-security">
                <FontAwesomeIcon icon={faLock} /> Your information is secure
              </div>

              <div className="summary-whatsapp">
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} /> Need help? WhatsApp us
                </a>
              </div>
            </motion.div>
          </div>
        </form>
      </div>

      <style>{`
        /* ========== HERO ========== */
        .checkout-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFFFFF;
          padding: 2.5rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .checkout-hero {
            padding: 3rem 2rem;
          }
        }
        .checkout-hero__overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }
        @media (min-width: 768px) {
          .checkout-hero__overlay {
            width: 500px;
            height: 500px;
          }
        }
        .checkout-hero__content {
          position: relative;
          z-index: 2;
        }
        .checkout-hero__icon {
          font-size: 2rem;
          color: #D9A321;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .checkout-hero__icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
          }
        }
        .checkout-hero h1 {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        .checkout-hero p {
          font-size: 0.938rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
        }
        @media (min-width: 768px) {
          .checkout-hero p {
            font-size: 1rem;
          }
        }

        /* ========== CONTAINER ========== */
        .checkout-container {
          padding: 2rem 1.5rem;
          max-width: 1280px;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .checkout-container {
            padding: 3rem 2rem;
          }
        }
        @media (max-width: 480px) {
          .checkout-container {
            padding: 1.5rem 0.75rem;
          }
        }

        .checkout-back {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #006B2E;
          text-decoration: none;
          margin-bottom: 2rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .checkout-back:hover {
          color: #D9A321;
          transform: translateX(-4px);
        }

        /* ========== GRID ========== */
        .checkout-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 768px) {
          .checkout-grid {
            grid-template-columns: 1fr 380px;
          }
        }

        /* ========== SECTIONS ========== */
        .checkout-section {
          background: #F8F9FA;
          padding: 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          border: 1px solid #e5e5e5;
        }
        @media (min-width: 768px) {
          .checkout-section {
            padding: 1.5rem;
          }
        }
        .checkout-section h3 {
          margin-bottom: 1rem;
          color: #006B2E;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Montserrat", sans-serif;
          font-size: 1.125rem;
        }
        .checkout-section h3 svg {
          color: #D9A321;
        }

        .form-group {
          margin-bottom: 1rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 0.25rem;
          font-weight: 500;
          font-size: 0.875rem;
        }
        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 0.625rem 0.75rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.938rem;
          font-family: "Inter", sans-serif;
          transition: border-color 0.3s ease;
          background: #ffffff;
          color: #1F1F1F;
        }
        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }
        .form-group textarea {
          resize: vertical;
          min-height: 60px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 480px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .payment-options {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .payment-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.875rem;
          background: #ffffff;
        }
        .payment-option:hover {
          border-color: #D9A321;
        }
        .payment-option.selected {
          border-color: #006B2E;
          background: #e8f5e9;
        }
        .payment-option input[type="radio"] {
          width: 16px;
          height: 16px;
          accent-color: #D9A321;
          cursor: pointer;
        }
        .payment-option svg {
          color: #006B2E;
          font-size: 0.875rem;
        }

        /* ========== SUMMARY ========== */
        .checkout-summary {
          background: #F8F9FA;
          padding: 1.25rem;
          border-radius: 12px;
          height: fit-content;
          position: sticky;
          top: 100px;
          border: 1px solid #e5e5e5;
        }
        @media (min-width: 768px) {
          .checkout-summary {
            padding: 1.5rem;
          }
        }
        .checkout-summary h3 {
          color: #006B2E;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-family: "Montserrat", sans-serif;
          font-size: 1.125rem;
        }
        .checkout-summary h3 svg {
          color: #D9A321;
        }

        .bulk-badge {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFF;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          text-align: center;
          font-size: 0.813rem;
          font-weight: 500;
        }

        .summary-items {
          max-height: 250px;
          overflow-y: auto;
        }
        .summary-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #eee;
          font-size: 0.875rem;
        }
        .summary-item:last-child {
          border-bottom: none;
        }

        .summary-divider {
          margin: 0.75rem 0;
          border: none;
          border-top: 1px solid #e5e5e5;
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
          font-size: 0.938rem;
        }
        .total-row.shipping {
          color: #5A5A5A;
          font-size: 0.875rem;
        }
        .total-row.supplier-info {
          font-size: 0.7rem;
          color: #5A5A5A;
          border-top: 1px solid #eee;
          padding-top: 0.5rem;
          margin-top: 0.5rem;
        }
        .total-row.supplier-info svg {
          color: #D9A321;
        }
        .total-row.grand-total {
          font-size: 1.1rem;
          font-weight: 700;
          color: #D9A321;
        }

        .summary-security {
          margin-top: 0.75rem;
          text-align: center;
          font-size: 0.75rem;
          color: #6B7280;
        }
        .summary-security svg {
          margin-right: 0.25rem;
        }

        .summary-whatsapp {
          margin-top: 0.75rem;
          text-align: center;
        }
        .summary-whatsapp a {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #25d366;
          text-decoration: none;
          font-size: 0.813rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .summary-whatsapp a:hover {
          text-decoration: underline;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 480px) {
          .checkout-section {
            padding: 0.875rem;
          }
          .payment-options {
            gap: 0.5rem;
          }
          .payment-option {
            font-size: 0.75rem;
            padding: 0.35rem 0.75rem;
          }
          .summary-item {
            font-size: 0.813rem;
          }
          .checkout-summary {
            padding: 0.875rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Checkout;